import { useState, useMemo, useEffect, type ElementType } from "react";
import { Link } from "react-router-dom";
import { usePagination } from "@/hooks/usePagination";
import { useProjectReadinessPage, useProjectReadinessSummary } from "@/hooks/useProjectServerPaging";
import { PaginationBar } from "@/components/PaginationBar";
import { useAppData } from "@/data/AppDataContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { formatShortDate } from "@/lib/dateFormat";
import { ExternalLink, CalendarDays, HardHat, PackageX, Wrench } from "lucide-react";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { ReadinessBadge } from "@/components/ReadinessBadge";
import { ReadinessGatingPanel } from "@/components/ReadinessGatingPanel";
import { MaterialGateBanner } from "@/components/scheduling/MaterialGateBanner";
import { JobSpecDriftBadge } from "@/components/job-detail/JobSpecDriftBadge";
import { PermitOperationalChip, PermitOperationalStrip } from "@/components/permitting/PermitOperationalStrip";
import { ProcurementOperationalChip, ProcurementOperationalStrip } from "@/components/procurement/ProcurementOperationalStrip";
import { MaterialReadinessChip } from "@/components/procurement/MaterialReadinessChip";
import { useMaterialGate } from "@/data/scheduling/useMaterialGate";
import { deriveReadinessFromJob, getCanonicalInstallReadiness } from "@/data/readiness";
import { getKit } from "@/data/materialKit";
import type { Job } from "@/data/types";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
  SectionEyebrow,
} from "@/components/cockpit/CockpitShell";
import CockpitHeaderPortal from "@/components/cockpit/CockpitHeaderPortal";
import { PROJECTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";

type ReadinessLane = "ready" | "almost" | "blocked" | "not-started" | "in-install";

interface EnrichedJob {
  job: Job;
  lane: ReadinessLane;
  materialPct: number;
}

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

const LANE_LABELS: Record<ReadinessLane, string> = {
  ready: "Ready",
  almost: "Almost",
  blocked: "Blocked",
  "not-started": "Not started",
  "in-install": "In install",
};

const TEMPLATE = "minmax(220px,1.3fr) minmax(170px,1fr) 100px 150px 120px 24px";

const IN_INSTALL_STAGES: Job["currentStage"][] = [
  "Assigned and Dispatched",
  "In Progress",
  "QA Review",
];

function getProductLabel(job: Job): string {
  const pc = job.projectConfig;
  const isUuidLike = (value: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
  const clean = (value: unknown): string => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (isUuidLike(v)) return "";
    return v
  };

  if (!pc) return clean(job.projectType) || "Project";
  const mat = clean(pc.primaryMaterial) || clean(pc.autoProjectType) || clean(job.projectType);
  const sys = clean(pc.bathroomSystemType) || clean(pc.autoProjectType) || clean(job.projectType);
  if (mat && sys && mat !== sys) return `${mat} ${sys}`;
  return mat || sys || "Project";
}

function laneTone(lane: ReadinessLane): "good" | "warn" | "bad" | "muted" {
  if (lane === "ready") return "good";
  if (lane === "almost") return "warn";
  if (lane === "blocked") return "bad";
  if (lane === "in-install") return "good";
  return "muted";
}

function getCanonicalMaterialPct(job: Job, buildReviewEvents: Parameters<typeof deriveReadinessFromJob>[1]): number {
  const material = deriveReadinessFromJob(job, buildReviewEvents).materials;
  if (material.status === "ready") return 100;
  if (material.status === "at_risk") return 50;
  return 0;
}

/**
 * Compact row indicator surfacing the canonical material gate verdict.
 * Renders only when the gate is meaningful (not idle/no_orders/ready).
 */
function RowGateChip({ jobId }: { jobId: string }) {
  const verdict = useMaterialGate(jobId);
  if (verdict.state === "no_orders" || verdict.state === "drafting" || verdict.state === "ready") return null;
  const meta =
    verdict.state === "blocked"
      ? { tone: "border-destructive/40 bg-destructive/10 text-destructive", label: "Gated" }
      : verdict.state === "gated"
      ? { tone: "border-warning/40 bg-warning/10 text-warning", label: "Gated" }
      : { tone: "border-muted-foreground/30 bg-muted/40 text-muted-foreground", label: "ETA pend" };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${meta.tone} flex items-center gap-1`} title={verdict.reason}>
      <PackageX className="h-2.5 w-2.5" /> {meta.label}
    </span>
  );
}

export default function InstallReadinessCockpit({
  embedded = false,
  headerTargetId,
  jobsOverride,
}: {
  embedded?: boolean;
  headerTargetId?: string;
  jobsOverride?: Job[];
} = {}) {
  const { jobs: appJobs, issues, buildReviewEvents, materialKits } = useAppData();
  const jobs = jobsOverride ?? appJobs;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeLane, setActiveLane] = useState<ReadinessLane | "all">("all");
  const [detailJob, setDetailJob] = useState<EnrichedJob | null>(null);
  const serverPaginationEnabled = PROJECTS_SERVER_PAGING_ENABLED;
  const [serverPage, setServerPage] = useState(0);
  const [serverPageSize, setServerPageSize] = useState(20);
  const serverRows = useProjectReadinessPage({
    enabled: serverPaginationEnabled,
    lane: activeLane,
    page: serverPage,
    pageSize: serverPageSize,
    search: debouncedSearch,
  });
  const serverSummary = useProjectReadinessSummary({ enabled: serverPaginationEnabled });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const enrichedJobs = useMemo<EnrichedJob[]>(() => {
    if (serverPaginationEnabled) return serverRows.rows;
    const opJobs = jobs.filter((j) => j.currentStage !== "Completed" && j.currentStage !== "Issue");
    return opJobs.map((job) => {
      const jobIssues = issues.filter((i) => i.jobId === job.id && i.status !== "Resolved");
      const kit = getKit(materialKits, job.id);
      const canonical = getCanonicalInstallReadiness(job, jobIssues, buildReviewEvents, kit);
      const materialPct = getCanonicalMaterialPct(job, buildReviewEvents);

      const isInInstall = IN_INSTALL_STAGES.includes(job.currentStage);
      const lane: ReadinessLane = isInInstall
        ? "in-install"
        : canonical.status === "Ready for Install"
        ? "ready"
        : canonical.status === "Partially Ready"
        ? "almost"
        : canonical.score <= 0
        ? "not-started"
        : "blocked";
      return { job, lane, materialPct };
    });
  }, [jobs, issues, buildReviewEvents, materialKits, serverPaginationEnabled, serverRows.rows]);

  useEffect(() => {
    if (!detailJob) return;
    const refreshed = enrichedJobs.find((entry) => entry.job.id === detailJob.job.id);
    if (refreshed && refreshed.job !== detailJob.job) {
      setDetailJob(refreshed);
    }
  }, [detailJob, enrichedJobs]);

  const filtered = useMemo(() => {
    if (serverPaginationEnabled) return enrichedJobs;
    let result = activeLane === "all" ? enrichedJobs : enrichedJobs.filter((e) => e.lane === activeLane);
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      result = result.filter((e) =>
        e.job.customerName.toLowerCase().includes(s) ||
        (e.job.jobNumber ?? "").toLowerCase().includes(s) ||
        e.job.id.toLowerCase().includes(s) ||
        getProductLabel(e.job).toLowerCase().includes(s) ||
        (e.job.address?.city ?? "").toLowerCase().includes(s) ||
        (e.job.assignedCrew ?? "").toLowerCase().includes(s)
      );
    }
    return result;
  }, [enrichedJobs, activeLane, debouncedSearch, serverPaginationEnabled]);

  const localPagination = usePagination(filtered, 20);

  useEffect(() => {
    if (serverPaginationEnabled) {
      setServerPage(0);
      return;
    }
    localPagination.reset();
  }, [debouncedSearch, activeLane, enrichedJobs, serverPaginationEnabled]);
  const serverTotalPages = Math.max(1, Math.ceil(serverRows.count / serverPageSize));
  const safeServerPage = Math.min(serverPage, serverTotalPages - 1);
  useEffect(() => {
    if (serverPaginationEnabled && serverPage !== safeServerPage) {
      setServerPage(safeServerPage);
    }
  }, [safeServerPage, serverPage, serverPaginationEnabled]);
  const pagination = serverPaginationEnabled
    ? {
        paged: filtered,
        page: safeServerPage,
        totalPages: serverTotalPages,
        totalItems: serverRows.count,
        hasNext: safeServerPage < serverTotalPages - 1,
        hasPrev: safeServerPage > 0,
        next: () => setServerPage((p) => Math.min(p + 1, serverTotalPages - 1)),
        prev: () => setServerPage((p) => Math.max(p - 1, 0)),
        reset: () => setServerPage(0),
        pageStart: serverRows.count > 0 ? safeServerPage * serverPageSize + 1 : 0,
        pageEnd: Math.min((safeServerPage + 1) * serverPageSize, serverRows.count),
        pageSize: serverPageSize,
        setPageSize: (size: number) => {
          setServerPageSize(size);
          setServerPage(0);
        },
      }
    : localPagination;

  const counts = useMemo(() => ({
    ready: serverPaginationEnabled && serverSummary.data ? serverSummary.data.ready : enrichedJobs.filter((e) => e.lane === "ready").length,
    almost: serverPaginationEnabled && serverSummary.data ? serverSummary.data.almost : enrichedJobs.filter((e) => e.lane === "almost").length,
    blocked: serverPaginationEnabled && serverSummary.data ? serverSummary.data.blocked : enrichedJobs.filter((e) => e.lane === "blocked").length,
    notStarted: serverPaginationEnabled && serverSummary.data ? serverSummary.data.notStarted : enrichedJobs.filter((e) => e.lane === "not-started").length,
    inInstall: serverPaginationEnabled && serverSummary.data ? serverSummary.data.inInstall : enrichedJobs.filter((e) => e.lane === "in-install").length,
    total: serverPaginationEnabled && serverSummary.data ? serverSummary.data.total : enrichedJobs.length,
  }), [enrichedJobs, serverPaginationEnabled, serverSummary.data]);

  const detailIsInInstall = detailJob ? IN_INSTALL_STAGES.includes(detailJob.job.currentStage) : false;
  const headerMetrics = (
    <>
      <HeroMetric label="Ready" value={counts.ready} hero tone="good" />
      <HeroDivider />
      <HeroMetric label="Almost" value={counts.almost} tone="warn" />
      <HeroMetric label="Blocked" value={counts.blocked} tone={counts.blocked > 0 ? "bad" : "default"} />
      <HeroMetric label="In install" value={counts.inInstall} tone="good" />
      <ReportFrictionButton moduleName="Install Readiness" />
    </>
  );

  const Wrapper: ElementType = embedded ? "div" : CockpitPage;
  return (
    <Wrapper>
      {embedded
        ? headerTargetId && <CockpitHeaderPortal targetId={headerTargetId}>{headerMetrics}</CockpitHeaderPortal>
        : <CockpitMasthead title="Install Readiness" right={headerMetrics} />}

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search by customer, job number, city, or installer" />}
        chips={<CockpitChipGroup value={activeLane} onChange={(v) => setActiveLane(v as ReadinessLane | "all")} options={[
          { value: "all", label: "All", count: counts.total },
          { value: "ready", label: "Ready", count: counts.ready },
          { value: "almost", label: "Almost", count: counts.almost },
          { value: "blocked", label: "Blocked", count: counts.blocked },
          { value: "not-started", label: "Not started", count: counts.notStarted },
          { value: "in-install", label: "In install", count: counts.inInstall },
        ]} />}
      />

      <CockpitListShell>
        <CockpitColumns template={TEMPLATE}>
          <span>Job</span><span>Product</span><span>Materials</span><span>Gate</span><span>Install</span><span />
        </CockpitColumns>
        {filtered.length === 0 ? <CockpitEmpty message="No jobs in this readiness view." /> : pagination.paged.map((ej) => (
          <CockpitRow key={ej.job.id} template={TEMPLATE} onClick={() => setDetailJob(ej)}>
            <div className="min-w-0">
              <div className="flex items-center gap-2"><StatusDot tone={laneTone(ej.lane)} /><p className="text-sm font-medium text-foreground truncate">{ej.job.customerName}</p></div>
              <InlineMeta items={[getJobDisplayRef(ej.job), ej.job.assignedCrew || "Unassigned"]} />
            </div>
            <p className="text-xs text-muted-foreground truncate">{getProductLabel(ej.job)}</p>
            <div className="space-y-1"><p className="text-xs text-foreground tabular-nums">{ej.materialPct}%</p><Progress value={ej.materialPct} className="h-1.5" /></div>
            <div className="flex items-center gap-1.5 flex-wrap" title="Gate + integrity signals">
              <Badge variant="outline" className="w-fit text-[10px]">{LANE_LABELS[ej.lane]}</Badge>
              <ReadinessBadge job={ej.job} />
              <RowGateChip jobId={ej.job.id} />
              <JobSpecDriftBadge job={ej.job} />
              <PermitOperationalChip job={ej.job} variant="readiness" hideWhenSilent />
              <ProcurementOperationalChip job={ej.job} variant="readiness" hideWhenSilent />
              <MaterialReadinessChip job={ej.job} variant="readiness" hideWhenSilent />
            </div>
            <p className="text-xs text-muted-foreground tabular-nums">{formatShortDate(ej.job.scheduledInstallDate) || "Not scheduled"}</p>
          </CockpitRow>
        ))}
      </CockpitListShell>
      <PaginationBar {...pagination} />

      <Dialog open={!!detailJob} onOpenChange={(o) => !o && setDetailJob(null)}>
        {detailJob && (
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{detailJob.job.customerName}</DialogTitle></DialogHeader>
            <div className="space-y-5">
              {/* Canonical material gate signal — primary readiness gate. Must remain visible. */}
              <MaterialGateBanner jobId={detailJob.job.id} />

              <ReadinessGatingPanel
                job={detailJob.job}
                issues={issues}
                operatorName="Install Readiness"
              />

              {/* Phase 2 — cross-domain permit operational summary */}
              {detailJob.job.permitRequired && <PermitOperationalStrip job={detailJob.job} />}

              {/* Phase 1 — cross-domain procurement operational summary */}
              <ProcurementOperationalStrip job={detailJob.job} />

              {/* Phase 3 — canonical install-readiness verdict (single deterministic gate) */}
              <div className="flex flex-wrap gap-2">
                <MaterialReadinessChip job={detailJob.job} variant="readiness" />
              </div>

              <div className="grid grid-cols-2 gap-px bg-border/60 border-y border-border/60">
                <div className="bg-background p-3"><SectionEyebrow>Project</SectionEyebrow><p className="text-sm text-foreground mt-1">{getProductLabel(detailJob.job)}</p></div>
                <div className="bg-background p-3"><SectionEyebrow>Install</SectionEyebrow><p className="text-sm text-foreground mt-1">{formatShortDate(detailJob.job.scheduledInstallDate) || "Not scheduled"}</p></div>
              </div>
              <div><div className="flex items-center justify-between mb-2"><SectionEyebrow>Material completion</SectionEyebrow><span className="text-xs text-foreground tabular-nums">{detailJob.materialPct}%</span></div><Progress value={detailJob.materialPct} className="h-2" /></div>

              <div className="flex items-center gap-2 pt-2 border-t border-border/60 flex-wrap">
                <Button variant="outline" size="sm" className="text-xs" asChild><Link to={`/jobs/${detailJob.job.id}`}><ExternalLink className="h-3 w-3 mr-1" /> View Job</Link></Button>
                <Button variant="outline" size="sm" className="text-xs" asChild>
                  <Link to={`/jobs/${detailJob.job.id}?tab=execution`}><Wrench className="h-3 w-3 mr-1" /> Open Job Execution</Link>
                </Button>
                {detailIsInInstall && (
                  <Button variant="outline" size="sm" className="text-xs" asChild>
                    <Link to="/installer-portal"><HardHat className="h-3 w-3 mr-1" /> Open Installer Portal</Link>
                  </Button>
                )}
                {detailJob.lane === "ready" && (
                  <Button size="sm" className="text-xs" asChild>
                    <Link to="/scheduling?lens=plan"><CalendarDays className="h-3 w-3 mr-1" /> Schedule</Link>
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </Wrapper>
  );
}
